import type { Country } from "@charterindex/armoury-www";
import type { AutocompleteChangeReason, PaperProps } from "@mui/material";
import type { JSX, SyntheticEvent } from "react";
import type { Api } from "../api";
import type { Attribute } from "../sign-up-request";
import type { App } from "../app";
import { COUNTRIES, COUNTRY_BY_CODE } from "@charterindex/armoury-www";
import { Autocomplete, css, Paper, Stack, styled, TextField, Typography } from "@mui/material";
import { bound, notify, observable, observe, useObservable } from "ecce-react";
import { forwardRef } from "react";
import { AttributeTextField, isUsState, usStateFromCode, UsStateSelect } from "../components";
import { SignUpStep } from "../step";
import { Debouncer, useAutofocus } from "../util";


export class AddressDetailsStep extends SignUpStep {
	override title: string = "Company Details";
	override view: () => JSX.Element = () => <AddressDetailsStepView step={ this }/>;

	@observable() accessor addressLine1 = "";
	@observable() accessor addressLine2 = "";

	#loadingAddresses: boolean = false;
	get loadingAddresses(): boolean { return this.#loadingAddresses; }
	@observable() private set loadingAddresses(value: boolean) { this.#loadingAddresses = value; }

	#addressOptions: Api.AddressSearchResult[] = [];
	get addressOptions(): Api.AddressSearchResult[] { return this.#addressOptions; }
	@observable() private set addressOptions(value: Api.AddressSearchResult[]) { this.#addressOptions = value; }

	#debounce = new Debouncer(150);

	get city(): Attribute<string> {
		return this.app.state.attribute("city");
	}
	get state(): Attribute<string> {
		return this.app.state.attribute("state");
	}
	get postcode(): Attribute<string> {
		return this.app.state.attribute("postcode");
	}
	get country(): Attribute<string> {
		return this.app.state.attribute("country");
	}

	constructor(app: App, previousStep?: SignUpStep) {
		super(app, previousStep);

		// Lat/lng is set when the user accepts an address from a MapBox suggestion.
		// If they later edit any address fields, we need to discard the lat/lng.
		observe(this, "addressLine1", app.state.resetLatLng);
		observe(this, "addressLine2", app.state.resetLatLng);
		observe(app.state.attribute("city"), app.state.resetLatLng);
		observe(app.state.attribute("country"), this.#handleCountryChange);
		observe(app.state.attribute("postcode"), app.state.resetLatLng);
		observe(app.state.attribute("state"), app.state.resetLatLng);
	}

	readonly #handleCountryChange = (): void => {
		this.app.state.resetLatLng();
		if(this.isUs && !isUsState(this.app.state.attribute("state").value)) {
			this.app.state.attribute("state").value = "";
		}

		notify(this, "isUs");
	};

	@bound()
	handleAddressLine1InputChange(event: React.SyntheticEvent, value: string | null): void {
		this.addressLine1 = value ?? "";
		this.addressOptions = [];

		if(!value) {
			this.#debounce.dispose();
			this.addressLine1 = "";
			return;
		}

		this.loadingAddresses = true;
		this.#debounce.execute(async() => {
			try {
				const response = await this.app.api.searchAddresses(value);
				if(!response.ok) {
					throw new Error(`Bad response: ${response.statusCode}: ${response.error.message}`);
				}
				if(this.addressLine1 === value) {
					this.addressOptions = response.data.results
						.filter(result => !!result.attributes.address);
				}
			} catch(err) {
				console.error("Failed to fetch addresses:", err); // eslint-disable-line no-console
			} finally {
				this.loadingAddresses = false;
			}
		});
	}

	@bound()
	handleAddressLine1Change(ev: SyntheticEvent, value: string | Api.AddressSearchResult | null, reason: AutocompleteChangeReason): void {
		if(typeof (value) === "string" || !value) {
			return;
		}

		switch(reason) {
			case "selectOption": {
				const [ addressLine1, addressLine2 ] = value.attributes.address?.split("\n") ?? "";
				this.addressLine1 = addressLine1 ?? "";
				this.addressLine2 = addressLine2 ?? "";
				this.city.value = value.attributes.city ?? "";
				this.state.value = value.attributes.state ? usStateFromCode(value.attributes.state) ?? value.attributes.state ?? "" : "";
				this.country.value = value.attributes.country ?? "";
				this.postcode.value = value.attributes.postcode ?? "";

				this.#fetchLatLong(value.id);

				break;
			}
		}
	}

	async #fetchLatLong(addressId: string): Promise<void> {
		try {
			const response = await this.app.api.getAddress(addressId);
			if(!response.ok) {
				throw new Error("Bad response: " + response.statusCode + response.error.message);
			}

			this.app.state.setLatLng(response.data.attributes.latitude, response.data.attributes.longitude);
		} catch(err) {
			console.error("Failed to fetch lat/lng: ", err); // eslint-disable-line no-console
		}
	}

	protected override async handleSubmit(): Promise<void> {
		this.app.state.attribute("address").value = (
			this.addressLine1 + "\n" + this.addressLine2
		).trim();
		await this.app.onAddressDetailsProvided();
	}

	@observable()
	get isUs(): boolean {
		return this.app.state.attribute("country").value === "US";
	}
}

const CompanyDetailsStepFields = styled("div")(({ theme }) => css`
	display: flex;
	flex-direction: column;
	gap: ${theme.spacing(1)};

	${theme.breakpoints.up("sm")} {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: ${theme.spacing(1)};
	}
	
	.fullWidth {
		grid-column: 1 / -1;
	}
	
	margin: auto 0;
`);

const AddressContent = (p: { opt: Api.AddressSearchResult; }): JSX.Element => {
	const [ addressLine1, addressLine2 ] = p.opt.attributes.address?.split("\n") ?? "";
	const secondary = [
		addressLine2,
		p.opt.attributes.city,
		p.opt.attributes.state,
		p.opt.attributes.postcode,
		p.opt.attributes.country,
	].filter(l => l?.length).join(", ");

	return (
		<Stack gap={ 0 }>
			<Typography component="span">
				{ addressLine1 }
			</Typography>
			<Typography component="span" variant="caption">
				{ secondary }
			</Typography>
		</Stack>
	);
};

const ResultsPaper = ({ children, ...props }: PaperProps) => {
	return (
		<Paper { ...props }>
			<Stack gap={ 0 }>
				<div>
					{ children }
				</div>
				<Typography
					component="a"
					href="https://www.mapbox.com/about/maps"
					variant="caption"
					padding={ 1 }
					textAlign="right"
					style={{ color: "inherit", textDecoration: "none" }}
				>
					<Stack direction="row" alignItems="center" justifyContent="flex-end">
						Powered by&nbsp;&nbsp;
						<svg height="16" viewBox="0 0 126 28" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M94.0376 7.66947C92.4586 7.66947 90.9434 8.31514 89.8429 9.44899V3.449C89.8429 3.26002 89.6834 3.10254 89.492 3.10254H87.3548C87.1634 3.10254 87.0039 3.26002 87.0039 3.449V19.6695C87.0039 19.8584 87.1634 20.0159 87.3548 20.0159H89.492C89.6834 20.0159 89.8429 19.8584 89.8429 19.6695V18.5514C90.9434 19.6852 92.4427 20.3309 94.0376 20.3309C97.371 20.3309 100.066 17.4962 100.066 14.0002C100.066 10.5041 97.371 7.66947 94.0376 7.66947ZM93.5432 17.7955C91.5176 17.7955 89.8748 16.1262 89.8589 14.0474V13.9529C89.8908 11.8742 91.5176 10.2049 93.5432 10.2049C95.5847 10.2049 97.2275 11.9057 97.2275 14.0002C97.2275 16.0947 95.5687 17.7955 93.5432 17.7955Z" fill="currentColor"/>
							<path d="M107.93 7.66919C104.325 7.66919 101.406 10.5038 101.406 13.9999C101.406 17.496 104.325 20.3306 107.93 20.3306C111.534 20.3306 114.453 17.496 114.453 13.9999C114.453 10.5038 111.534 7.66919 107.93 7.66919ZM107.914 17.7952C105.872 17.7952 104.229 16.0944 104.229 13.9999C104.229 11.9054 105.888 10.2046 107.914 10.2046C109.939 10.2046 111.598 11.9054 111.598 13.9999C111.598 16.0944 109.939 17.7952 107.914 17.7952Z" fill="currentColor"/>
							<path d="M68.0398 7.98415H65.9026C65.7112 7.98415 65.5517 8.14163 65.5517 8.33061V9.44872C64.4512 8.31486 62.9519 7.66919 61.357 7.66919C58.0236 7.66919 55.3281 10.5038 55.3281 13.9999C55.3281 17.496 58.0236 20.3306 61.357 20.3306C62.936 20.3306 64.4512 19.6849 65.5517 18.5511V19.6692C65.5517 19.8582 65.7112 20.0156 65.9026 20.0156H68.0398C68.2312 20.0156 68.3907 19.8582 68.3907 19.6692V8.33061C68.3907 8.14163 68.2312 7.98415 68.0398 7.98415ZM61.8514 17.7952C59.8099 17.7952 58.1671 16.0944 58.1671 13.9999C58.1671 11.9054 59.8259 10.2046 61.8514 10.2046C63.877 10.2046 65.5198 11.8739 65.5357 13.9527V14.0471C65.5198 16.1259 63.877 17.7952 61.8514 17.7952Z" fill="currentColor"/>
							<path d="M78.7104 7.66919C77.1315 7.66919 75.6163 8.31486 74.5158 9.44872V8.33061C74.5158 8.14163 74.3563 7.98415 74.1649 7.98415H72.0276C71.8363 7.98415 71.6768 8.14163 71.6768 8.33061V24.5511C71.6768 24.7401 71.8363 24.8975 72.0276 24.8975H74.1649C74.3563 24.8975 74.5158 24.7401 74.5158 24.5511V18.5511C75.6163 19.6849 77.1155 20.3306 78.7104 20.3306C82.0439 20.3306 84.7393 17.496 84.7393 13.9999C84.7393 10.5038 82.0439 7.66919 78.7104 7.66919ZM78.216 17.7952C76.1904 17.7952 74.5477 16.1259 74.5317 14.0471V13.9527C74.5636 11.8739 76.1904 10.2046 78.216 10.2046C80.2575 10.2046 81.9003 11.9054 81.9003 13.9999C81.9003 16.0944 80.2575 17.7952 78.216 17.7952Z" fill="currentColor"/>
							<path d="M48.9329 7.66915C47.338 7.68489 45.8866 8.59828 45.2008 10.0314C44.4193 8.56678 42.8562 7.6534 41.1815 7.66915C39.8737 7.66915 38.6456 8.29907 37.88 9.33844V8.33056C37.88 8.14159 37.7205 7.98411 37.5291 7.98411H35.3919C35.2005 7.98411 35.041 8.14159 35.041 8.33056V19.6691C35.041 19.8581 35.2005 20.0156 35.3919 20.0156H37.5291C37.7205 20.0156 37.88 19.8581 37.88 19.6691V12.8817C37.9598 11.3699 39.0284 10.1573 40.3362 10.1573C41.6919 10.1573 42.8243 11.2754 42.8243 12.74V19.6691C42.8243 19.8581 42.9838 20.0156 43.1752 20.0156H45.3284C45.5198 20.0156 45.6793 19.8581 45.6793 19.6691L45.6633 12.614C45.8547 11.2282 46.8595 10.1573 48.0876 10.1573C49.4433 10.1573 50.5757 11.2754 50.5757 12.74V19.6691C50.5757 19.8581 50.7352 20.0156 50.9266 20.0156H53.0798C53.2712 20.0156 53.4307 19.8581 53.4307 19.6691L53.4147 11.8739C53.4147 9.54316 51.4051 7.66915 48.9329 7.66915Z" fill="currentColor"/>
							<path d="M125.953 19.5276L122.252 13.9685L125.921 8.45669C126.016 8.31496 125.969 8.11024 125.825 8.01575C125.777 7.98425 125.713 7.96851 125.65 7.96851H123.177C122.986 7.96851 122.811 8.06299 122.715 8.22047L120.562 11.7953L118.409 8.23622C118.313 8.07874 118.137 7.98425 117.946 7.98425H115.474C115.298 7.98425 115.155 8.12599 115.155 8.29921C115.155 8.36221 115.171 8.4252 115.203 8.47244L118.871 13.9843L115.171 19.5433C115.075 19.685 115.123 19.8898 115.267 19.9843C115.314 20.0158 115.378 20.0315 115.442 20.0315H117.914C118.106 20.0315 118.281 19.937 118.377 19.7795L120.578 16.1575L122.779 19.7795C122.874 19.937 123.05 20.0315 123.241 20.0315H125.682C125.857 20.0315 126.001 19.8898 126.001 19.7165C126.001 19.6535 125.985 19.5906 125.953 19.5276Z" fill="currentColor"/>
							<path d="M14.179 0C6.34786 0 0 6.26772 0 14C0 21.7323 6.34786 28 14.179 28C22.0102 28 28.358 21.7323 28.358 14C28.358 6.26772 22.0102 0 14.179 0ZM20.9097 17.4331C16.061 22.2205 7.40052 20.6929 7.40052 20.6929C7.40052 20.6929 5.83748 12.1575 10.702 7.35433C13.3975 4.69291 17.8633 4.80315 20.6864 7.5748C23.5094 10.3465 23.6051 14.7717 20.9097 17.4331Z" fill="currentColor"/>
							<path d="M15.806 8.20459L14.4184 11.0235L11.5635 12.3936L14.4184 13.7636L15.806 16.5825L17.1936 13.7636L20.0486 12.3936L17.1936 11.0235L15.806 8.20459Z" fill="currentColor"/>
						</svg>
					</Stack>
				</Typography>
			</Stack>
		</Paper>
	);
};

const AddressFields = forwardRef<HTMLElement, { step: AddressDetailsStep; }>((p, ref): JSX.Element => {
	useObservable(p.step, "addressLine1");
	useObservable(p.step, "addressLine2");
	useObservable(p.step, "loadingAddresses");
	useObservable(p.step, "addressOptions");

	return (
		<>
			<Autocomplete<Api.AddressSearchResult, false, false, true>
				className="fullWidth"
				freeSolo
				renderInput={ props => (
					<TextField
						{ ...props }
						label={ "Address Line 1" }
						required
						autoComplete="address-line1"
						inputRef={ ref }
					/>
				) }
				loading={ p.step.loadingAddresses }
				noOptionsText="No addresses found"
				value={ p.step.addressLine1 }
				onInputChange={ p.step.handleAddressLine1InputChange }
				onChange={ p.step.handleAddressLine1Change }
				options={ p.step.addressOptions }
				getOptionKey={ opt => typeof (opt) === "string" ? opt : opt.id }
				getOptionLabel={ opt => typeof (opt) === "string" ? opt : "" }
				slots={{ paper: ResultsPaper }}
				renderOption={ ({ key, ...props }, opt) => {
					if(typeof (opt) === "string") {
						return <li { ...props }>{ opt }</li>;
					}

					return (
						<li { ...props } key={ key }>
							<AddressContent opt={ opt }/>
						</li>
					);
				} }
				filterOptions={ opt => opt }
				size="small"
				disablePortal
			/>

			<TextField
				className="fullWidth"
				label={ "Address Line 2" }
				value={ p.step.addressLine2 }
				onChange={ ev => p.step.addressLine2 = ev.target.value }
				size="small"
				autoComplete="address-line2"
			/>
		</>
	);
});

const CountryAttributeField = (p: { attribute: Attribute<string>; }): JSX.Element => {
	useObservable(p.attribute);

	return (
		<Autocomplete<Country>
			options={ COUNTRIES }
			getOptionKey={ opt => opt.code }
			getOptionLabel={ opt => opt.name }
			value={ COUNTRY_BY_CODE[p.attribute.value] ?? null }
			onChange={ (ev, value) => p.attribute.value = value?.code ?? "" }
			renderInput={ props => (
				<TextField
					{ ...props }
					label="Country *"
					InputProps={{
						...props.InputProps,
						required: true,
					}}
					autoComplete="country"
				/>
			) }
			disablePortal
			size="small"
		/>
	);
};

const AddressDetailsStepView = (p: { step: AddressDetailsStep; }): JSX.Element => {
	const autofocus = useAutofocus();
	useObservable(p.step, "isUs");

	return (
		<>
			<Typography component="p">
				Provide your address.
			</Typography>

			<CompanyDetailsStepFields>
				<AddressFields
					ref={ autofocus }
					step={ p.step }
				/>
				<AttributeTextField
					label="City"
					attribute={ p.step.city }
					required
					size="small"
					autoComplete="address-level2"
				/>
				{ !p.step.isUs && (
					<AttributeTextField
						label="State"
						attribute={ p.step.state }
						size="small"
						autoComplete="address-level1"
					/>
				) }
				{ p.step.isUs && (
					<UsStateSelect
						label="State"
						attribute={ p.step.state }
						size="small"
						autoComplete="address-level1"
					/>
				) }
				<AttributeTextField
					label="Zip / Postcode"
					attribute={ p.step.postcode }
					required
					size="small"
					autoComplete="postal-code"
				/>
				<CountryAttributeField
					attribute={ p.step.country }
				/>
			</CompanyDetailsStepFields>
		</>
	);
};
